import React from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { SidebarIcon } from '../../../shared/components/SidebarIcon';
import { SidebarLayoutEditor } from '../../../shared/components/SidebarLayoutEditor';
import {
  addSidebarGroup,
  addSidebarLink,
  applySidebarLayout,
  DEFAULT_SIDEBAR_LAYOUT,
  isSidebarLayoutCustomized,
  moveSidebarEntryTo,
  removeSidebarGroup,
  removeSidebarLink,
  renameSidebarGroup,
  setSidebarItemHidden,
  SidebarLayout,
} from '../../../shared/components/sidebarLayout';
import { useWorkspaceNavigationSettings } from '../../../shared/components/useWorkspaceNavigationSettings';
import { WORKSPACE_NAV_ITEMS, WorkspaceNavItem } from '../../../shared/components/workspaceNavItems';
import { useAuth } from '../../auth/context/AuthContext';
import styles from './WorkspaceNavigationPage.module.css';

const MAX_LABEL_LENGTH = 40;

const cleanLabels = (labels: Record<string, string>): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const item of WORKSPACE_NAV_ITEMS) {
    const value = (labels[item.id] ?? '').trim();
    if (value) {
      result[item.id] = value;
    }
  }
  return result;
};

const sameLabels = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const cleanA = cleanLabels(a);
  const cleanB = cleanLabels(b);
  const keys = new Set([...Object.keys(cleanA), ...Object.keys(cleanB)]);
  return Array.from(keys).every(key => cleanA[key] === cleanB[key]);
};

const sameLayout = (a: SidebarLayout | null, b: SidebarLayout | null): boolean =>
  JSON.stringify(a ?? DEFAULT_SIDEBAR_LAYOUT) === JSON.stringify(b ?? DEFAULT_SIDEBAR_LAYOUT);

export const WorkspaceNavigationPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'nav']);
  const { auth } = useAuth();
  const tenantId = auth?.tenantId ?? null;
  const { settings, loaded, save } = useWorkspaceNavigationSettings(tenantId);

  const [labels, setLabels] = React.useState<Record<string, string>>({});
  const [layout, setLayout] = React.useState<SidebarLayout>(DEFAULT_SIDEBAR_LAYOUT);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const baselineKey = React.useRef<string | null>(null);

  // Seed the draft from the saved settings once per saved version, so a save (or a load) does
  // not clobber what the admin is typing.
  React.useEffect(() => {
    if (!loaded) {
      return;
    }

    const key = `${tenantId ?? ''}|${settings.updatedAt ?? ''}`;
    if (baselineKey.current === key) {
      return;
    }

    baselineKey.current = key;
    setLabels({ ...settings.labelOverrides });
    setLayout(settings.defaultLayout ?? DEFAULT_SIDEBAR_LAYOUT);
  }, [loaded, settings, tenantId]);

  const items = WORKSPACE_NAV_ITEMS;
  const applied = React.useMemo(() => applySidebarLayout(items, layout), [items, layout]);
  const builtInLabel = React.useCallback((item: WorkspaceNavItem) => t(item.labelKey, { ns: 'nav' }), [t]);
  const labelFor = React.useCallback(
    (item: WorkspaceNavItem) => (labels[item.id] ?? '').trim() || builtInLabel(item),
    [builtInLabel, labels],
  );

  const dirty = loaded && (
    !sameLabels(labels, settings.labelOverrides)
    || !sameLayout(isSidebarLayoutCustomized(layout) ? layout : null, settings.defaultLayout)
  );

  const updateLayout = (updater: (current: SidebarLayout) => SidebarLayout) => {
    setLayout(current => updater(current));
    setNotice(null);
  };

  const onDiscard = () => {
    setLabels({ ...settings.labelOverrides });
    setLayout(settings.defaultLayout ?? DEFAULT_SIDEBAR_LAYOUT);
    setError(null);
    setNotice(null);
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await save({
        labelOverrides: cleanLabels(labels),
        defaultLayout: isSidebarLayoutCustomized(layout) ? layout : null,
      });
      setNotice(t('workspaceNavigation.saved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('workspaceNavigation.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="company-admin-page">
        <Spinner text={t('workspaceNavigation.loading')} />
      </div>
    );
  }

  return (
    <div className={`company-admin-page ${styles.page}`}>
      <Breadcrumb items={[{ label: t('workspaceNavigation.breadcrumb'), path: '/admin/navigation' }]} />

      <div>
        <h2>{t('workspaceNavigation.title')}</h2>
        <p className={styles.intro}>{t('workspaceNavigation.intro')}</p>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="success">{notice}</div> : null}

      <section className="company-section">
        <h3>{t('workspaceNavigation.labelsTitle')}</h3>
        <p className={styles.help}>{t('workspaceNavigation.labelsHelp')}</p>
        <div className={styles.labelGrid}>
          {items.map(item => {
            const builtIn = builtInLabel(item);
            return (
              <label key={item.id} className={styles.labelRow}>
                <span className={styles.labelIcon} aria-hidden><SidebarIcon name={item.icon} /></span>
                <span className={styles.labelMeta}>
                  <span className={styles.labelName}>{builtIn}</span>
                  <span className={styles.labelBuiltIn}>{t('workspaceNavigation.builtInLabel', { label: builtIn })}</span>
                </span>
                <input
                  className={`text-input ${styles.labelInput}`}
                  value={labels[item.id] ?? ''}
                  maxLength={MAX_LABEL_LENGTH}
                  placeholder={builtIn}
                  aria-label={t('workspaceNavigation.labelInput', { label: builtIn })}
                  onChange={event => {
                    const value = event.target.value;
                    setLabels(current => ({ ...current, [item.id]: value }));
                    setNotice(null);
                  }}
                />
              </label>
            );
          })}
        </div>
      </section>

      <section className="company-section">
        <h3>{t('workspaceNavigation.layoutTitle')}</h3>
        <p className={styles.help}>{t('workspaceNavigation.layoutHelp')}</p>
        <div className={styles.layoutEditor}>
          <SidebarLayoutEditor
            visible={applied.visible}
            hidden={applied.hidden}
            canReset={isSidebarLayoutCustomized(layout)}
            labelFor={labelFor}
            hint={t('workspaceNavigation.layoutHint')}
            resetLabel={t('workspaceNavigation.layoutReset')}
            onMove={(id, target) => updateLayout(current => moveSidebarEntryTo(items, current, id, target))}
            onSetHidden={(id, hidden) => updateLayout(current => setSidebarItemHidden(items, current, id, hidden))}
            onAddGroup={(id, label) => updateLayout(current => addSidebarGroup(items, current, id, label))}
            onRenameGroup={(id, label) => updateLayout(current => renameSidebarGroup(current, id, label))}
            onRemoveGroup={id => updateLayout(current => removeSidebarGroup(items, current, id))}
            onAddLink={(label, url) => updateLayout(current => addSidebarLink(items, current, label, url))}
            onRemoveLink={id => updateLayout(current => removeSidebarLink(items, current, id))}
            onReset={() => updateLayout(() => DEFAULT_SIDEBAR_LAYOUT)}
          />
        </div>
      </section>

      <div className={styles.footer}>
        {dirty ? <span className={styles.dirty}>{t('workspaceNavigation.unsavedChanges')}</span> : <span />}
        <div className={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={onDiscard} disabled={!dirty || saving}>
            {t('workspaceNavigation.discard')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void onSave()} disabled={!dirty || saving}>
            {saving ? t('workspaceNavigation.saving') : t('workspaceNavigation.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceNavigationPage;
